/* eslint-disable office-addins/no-context-sync-in-loop */
/* global window document console localStorage URLSearchParams atob TextDecoder */
import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { CompoundButton, Spinner } from "@fluentui/react-components";

import {
  openStyleSheetDialog,
  openStyleSheetWizard,
  toGridd,
  toTemplates /*, toWelcome */,
  setLocalStorageKey,
  setReadOnlyMode,
} from "../state/appStateSlice";
import { updateCell } from "../state/structureSlice";
import { reset as selectionReset } from "../state/selectionSlice";
import logo from "../assets/icons/logo-64x64.svg";
import { CollectionsAdd20Filled } from "@fluentui/react-icons";
import { loadSheets } from "../state/styleSheetSlice";
import PropTypes from "prop-types";
import exists from "../helpers/exists";
import { parseShapes } from "../ppt/baking/parseShapes";
import {
  safeGetCurrentSlideId,
  safeLoadGriddFromShape,
  safeLoadTagFromDocument,
  safeLoadTagFromSlide,
} from "../ppt/safePPT";
import {
  requestInlineDataFromParent,
  isLocalhostUri,
  isEditableInlineOrigin,
  loadGriddObject,
  loadFromAssets,
  loadFromURI,
} from "../helpers/loading";
import { upgradeColors } from "../state/upgrades/colorsUpgrade";

const Startup = ({ presId, slideId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isLoaded, setIsLoaded] = useState(false);
  const [diffs, setDiffs] = useState([]);
  const welcomeTemplate = useSelector((state) => state.templates.welcome);
  const currentSlideId = useSelector((state) => state.app.currentSlideId);

  useEffect(() => {
    dispatch(selectionReset());
    try {
      handleLoad();
    } catch (e) {
      console.error(e);
    }
  }, []);

  async function handleLoad() {
    let shapesChanged = false;
    let style = null;
    let gridd = null;

    const urlParams = new URLSearchParams(window.location.search);
    const griddDataId = urlParams.get("gridd_data_id");
    const griddInline = urlParams.get("gridd_inline");
    const griddURI = urlParams.get("gridd_data_uri");
    const readOnly = urlParams.get("gridd_readonly");
    const griddEditable = urlParams.get("gridd_editable");
    const styleURI = urlParams.get("gridd_style_uri");

    // Non-inline sources decide read-only synchronously. The inline case is
    // decided after the handshake (below), where the real parent origin is known
    // — document.referrer is too fragile to gate editability on.
    if (readOnly || (griddURI && !isLocalhostUri(griddURI))) {
      dispatch(setReadOnlyMode(true));
    }

    // load raw data from whichever source it comes, first gridd then stylesheet

    if (exists("PowerPoint")) {
      if (!presId || !slideId || presId === "PRES" || slideId === "SLIDE") {
        // try refresh captured slide ID, a possible fix for load delays on O365 and desktop PPTs
        const currentSlide = await safeGetCurrentSlideId();
        presId = currentSlide.presId;
        slideId = currentSlide.slideId;
      }

      console.log("try loading gridd from icon shape, fallback to slide tags");
      gridd = await safeLoadGriddFromShape(currentSlideId ?? slideId);
      if (!gridd) gridd = await safeLoadTagFromSlide(currentSlideId ?? slideId, "GRIDD");

      const diffs = await parseShapes(slideId, griddObject);
      shapesChanged = diffs.length > 0;
      if (shapesChanged) {
        setDiffs(diffs);
        console.log("shape changes found", diffs);
      }
    } else if (griddDataId) {
      console.log("gridd_data_id found in url, loading from assets");
      gridd = await loadFromAssets(griddDataId);
    } else if (griddInline) {
      console.log("gridd_inline found in url, requesting data from parent via postMessage");
      const inline = await requestInlineDataFromParent();
      const bytes = new Uint8Array([...atob(inline.data)].map((c) => c.charCodeAt(0)));
      gridd = new TextDecoder().decode(bytes);
      // Editable inline is opt-in (gridd_editable=1) and validated against the
      // real handshake origin. Anything not explicitly trusted stays read-only.
      if (!isEditableInlineOrigin(inline.origin, griddEditable)) {
        dispatch(setReadOnlyMode(true));
      }
    } else if (griddURI) {
      console.log(`gridd_data_uri found in url, loading from ${griddURI}`);
      gridd = await loadFromURI(griddURI);
      dispatch(setLocalStorageKey(griddURI + "_GRIDD"));
    }
    // full fallback to local storage gridd in any case
    if (!gridd) {
      console.log("gridd not found on slide, try loading from local storage");
      if (exists("PowerPoint")) {
        gridd = localStorage.getItem(presId + (currentSlideId ?? slideId) + "_GRIDD");
      } else {
        gridd = localStorage.getItem("PRESSLIDE_GRIDD");
      }
    }

    if (exists("PowerPoint")) {
      console.log(`try loading stylesheet from document tag`);
      style = await safeLoadTagFromDocument("GRIDD_STYLESHEET");
    } else if (styleURI) {
      console.log(`gridd_style_uri found in url, loading from ${styleURI}`);
      style = await loadFromURI(styleURI);
    }
    // full fallback to local storage style in any case
    if (!style && !exists("PowerPoint")) {
      console.log("trying local storage contains stylesheet");
      style = localStorage.getItem("STYLESHEETS");
    }

    const griddObject = JSON.parse(gridd ?? "{}");
    const styleObject = JSON.parse(style ?? "{}");

    if (gridd && style) {
      // upgrade data before dispatch
      try {
        console.log("Upgrading", { griddObject, styleObject });
        const upgraded = upgradeColors(griddObject, styleObject);
        // Note: in future, any data structure upgrade to be chained here!
        console.log("Upgraded", upgraded);
      } catch (e) {
        console.error(e);
      }
    }

    // dispatch state changes to Gridd and stylesheet

    if (gridd) loadGriddObject(dispatch, griddObject);
    if (style) dispatch(loadSheets(styleObject));

    // decision making what page to show next
    if (gridd && style === null && exists("PowerPoint")) {
      dispatch(openStyleSheetDialog());
      dispatch(openStyleSheetWizard());
    }

    if (shapesChanged) {
      // only valid if we are in PowerPoint
      console.log("shape texts changed, showing choice for merge");
    } else if (gridd) {
      console.log("navigating to loaded gridd");
      dispatch(toGridd());
    } else if (!localStorage.getItem("WELCOMED")) {
      console.log("importing welcome table and showing popup");
      const griddObject = welcomeTemplate;
      loadGriddObject(dispatch, griddObject);
      localStorage.setItem("WELCOMED", "true");
      dispatch(toGridd());
    } else {
      console.log("importing from templates", { griddExists: !!gridd, stylesExists: !!style });
      dispatch(toTemplates({ griddContentExist: !!gridd, styleContentExist: !!style }));
    }

    setIsLoaded(true);
    document.getElementById("loading-container").style.display = "none";
  }

  return !isLoaded ? (
    <Spinner />
  ) : (
    <div
      id="container"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        alignContent: "center",
      }}
    >
      <div
        id="template_items"
        style={{
          display: "grid",
          height: "90%",
          gridTemplateColumns: "1fr 1fr 1fr",
          gridTemplateRows: "1fr 2fr 1fr",
        }}
      >
        <div key={"top 1"} />
        <div key={"top 2"} />
        <div key={"top 3"} />

        <div
          key={"from_gridd"}
          style={{
            alignContent: "center",
            width: "100%",
            height: "100%",
            padding: 2,
          }}
        >
          <CompoundButton
            icon={<CollectionsAdd20Filled />}
            secondaryContent={t("continue.gridd.subtitle")}
            appearance="subtle"
            shape="rounded"
            size="large"
            style={{
              width: "80%",
              height: "80%",
              margin: 24,
            }}
            onClick={() => {
              dispatch(toGridd());
            }}
          >
            {t("continue.gridd.title")}
          </CompoundButton>
        </div>

        <div
          key={"from_shapes"}
          style={{
            alignContent: "center",
            width: "100%",
            height: "100%",
            padding: 2,
          }}
        >
          <CompoundButton
            icon={<CollectionsAdd20Filled />}
            secondaryContent={t("continue.shapes.subtitle") + " (" + diffs?.length + ")"}
            appearance="subtle"
            shape="rounded"
            size="large"
            style={{
              width: "80%",
              height: "80%",
              margin: 24,
            }}
            onClick={() => {
              // apply from shapeTexts, no batch so it can be individually undo-ed
              diffs.forEach((diff) => {
                dispatch(updateCell({ x: diff.x, y: diff.y, text: diff.shapeText }));
              });
              dispatch(toGridd());
            }}
          >
            {t("continue.shapes.title")}
          </CompoundButton>
        </div>

        <div
          key={"templates"}
          style={{
            alignContent: "center",
            width: "100%",
            height: "100%",
            padding: 2,
          }}
        >
          <CompoundButton
            icon={
              <img
                src={logo}
                style={{
                  width: 24,
                  height: 24,
                }}
              />
            }
            secondaryContent={t("continue.templates.subtitle")}
            appearance="subtle"
            shape="rounded"
            size="large"
            style={{
              width: "80%",
              height: "80%",
              margin: 24,
            }}
            onClick={() => {
              dispatch(toTemplates());
            }}
          >
            {t("continue.templates.title")}
          </CompoundButton>
        </div>

        <div key={"bot 1"} />
        <div key={"bot 2"} />
        <div key={"bot 3"} />
      </div>
    </div>
  );
};

Startup.propTypes = {
  presId: PropTypes.string,
  slideId: PropTypes.string,
};

export default Startup;
